import StepExecutorFactory from "./StepExecutorFactory";
import StepConfigFactory from "./StepConfigFactory";
import DateUtil from "../utils/DateUtil";
import GpsManagerWrapper from "../gps/manager/GpsManagerWrapper";

export const SCRIPT_FAILED = 'SCRIPT_FAILED';
export const SCRIPT_STARTED = 'SCRIPT_STARTED';
export const ITERATION_STARTED = 'ITERATION_STARTED';
export const STEP_STARTED = 'STEP_STARTED';
export const STEP_PROGRESS = 'STEP_PROGRESS';
export const STEP_DONE = 'STEP_DONE';
export const ITERATION_DONE = 'ITERATION_DONE';
export const SCRIPT_DONE = 'SCRIPT_DONE';

export const NOT_AVAILABLE = -1;

/**
 * Run a specific script
 */
export default class ScriptEngine {

    /**
     *
     * @param {string} uid the unique id
     * @param {number} campaignId from config
     * @param {ConfigScenario} scenario the scenario config
     * @param {GpsManager} gpsManager the GPS manager to collect location
     * @param {DeviceManager} deviceManager the device manager to collect device informations
     * @param {function} callback the callback
     * @param {Map<string,string>} customFields A map of custom fields
     * @param {number} group identifier
     * @param {GatewayService} gateway service
     */
    constructor(uid, campaignId, scenario, gpsManager, deviceManager, callback, customFields, groupId, gatewayService) {
        this.uid = uid;
        this.campaignId = campaignId;
        this.scenario = scenario;
        this.scenarioId = DateUtil.getCurrentTimestamp();
        this.running = false;
        this.cancelled = false;
        this.gpsManager = gpsManager;
        this.deviceManager = deviceManager;
        this.gatewayService = gatewayService;

        if (callback) {
            this.callback = callback;
        } else {
            this.callback = () => {
            };
        }
        this.stepExecutor = null;
        this.customFields = customFields;
        this.groupId = groupId;
    }

    /**
     * Start the script
     */
    start() {
        if (this.running) {
            this.callback(SCRIPT_FAILED, NOT_AVAILABLE, "Script already started");
            return;
        }
        if (this.scenario.steps.length === 0) {
            this.callback(SCRIPT_FAILED, NOT_AVAILABLE, "No step to run");
            return;
        }
        this.running = true;
        this.callback(SCRIPT_STARTED);
        this.runIteration(this.scenario);
    }

    /**
     * Run a new iteration
     * @param {ConfigScenario} scenario the scenario config
     * @param {number} iteration the iteration index
     */
    runIteration(scenario, iteration = 0) {
        if (this.cancelled) {
            this.testDone();
            return;
        }
        if (scenario.iterations > 0 && iteration >= scenario.iterations) {
            // No more iteration, script is done
            this.testDone();
            return;
        }
        this.callback(ITERATION_STARTED, iteration);
        this.runStep(scenario, iteration);
    }

    /**
     * Run a new step
     * @param {ConfigScenario} scenario the scenario config
     * @param {number} iteration the iteration index
     * @param {number} step the step index
     */
    runStep(scenario, iteration, step = 0) {
        if (this.cancelled) {
            this.testDone();
            return;
        }
        if (step > scenario.steps.length) {
            // Start the next iteration
            this.callback(ITERATION_DONE, iteration);
            this.runIteration(scenario, iteration + 1);
            return;
        }
        this.stepExecutor = this.getStepExecutor(scenario, step);
        if (!this.stepExecutor) {
            this.runStep(scenario, iteration, step + 1);
            return;
        }
        this.callback(STEP_STARTED, step, StepConfigFactory.getStepPublicConfig(scenario.steps[step]));
        this.stepExecutor.start((data) => {
            this.callback(STEP_PROGRESS, step, data);
        }).then((result) => {
            this.stepExecutor = null;
            this.callback(STEP_DONE, step, result);
            this.runStep(scenario, iteration, step + 1);
        }).catch((error) => {
            this.stepExecutor = null;
            this.callback(STEP_DONE, step, error);
            this.runStep(scenario, iteration, step + 1);
        });
    }

    testDone() {
        this.running = false;
        this.callback(SCRIPT_DONE);
    }

    /**
     * Method only for unit test
     *
     * @param {ConfigScenario} scenario
     * @param {number} stepIndex
     */
    getStepExecutor(scenario, stepIndex) {
        const stepConfig = scenario.steps[stepIndex];
        return StepExecutorFactory.getStepExecutor(new GpsManagerWrapper(this.gpsManager, scenario.gpsConfig), this.deviceManager, stepConfig, this.uid, this.campaignId, this.scenarioId, this.customFields, this.groupId, this.gatewayService);
    }

    /**
     * Stop the test
     * @return {boolean} true if the test will be stopped, false if doesn't running or already cancelled
     */
    stop() {
        if (this.running && !this.cancelled && this.stepExecutor.stop && this.stepExecutor.stop()) {
            this.cancelled = true;
            return true;
        }
        return false;
    }
}


// WEBPACK FOOTER //
// ./js/core/script/ScriptEngine.js